import { definePreset } from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";

const BrandPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: "var(--kedo-color-blue-5)", 100: "var(--kedo-color-blue-4)",
            200: "var(--kedo-color-blue-3)", 300: "var(--kedo-color-blue-3)",
            400: "var(--kedo-color-accent-blue)", 500: "var(--kedo-color-blue-1)",
            600: "var(--kedo-color-blue-1)", 700: "var(--kedo-color-blue-1)",
            800: "var(--kedo-color-dark-2)", 900: "var(--kedo-color-dark-2)",
            950: "var(--kedo-color-dark-1)",
        },
        borderRadius: { none: "0", xs: "var(--kedo-radius-xs)", sm: "var(--kedo-radius-sm)", md: "var(--kedo-radius-default)", lg: "var(--kedo-radius-md)", xl: "var(--kedo-radius-lg)" },
        // Rows inside an overlay (Select/MultiSelect options and anything else on
        // the shared `list` tokens). 12px is the ceiling here, not a preference:
        // the panel is 16px with a 4px inset, and a nested radius above 16−4 starts
        // fighting the panel's own corners. BaseDropdownOption and BaseTitleList
        // carry the same value as a Tailwind class.
        // `padding` is the breathing room between the options and the panel's own
        // border; Aura's 4px read as cramped once the panel took the branded
        // radius. Shared by Select/MultiSelect and BaseDropdown's panel, which
        // takes its content padding from this same token.
        list: { padding: "0.5rem", option: { borderRadius: "var(--kedo-radius-default)" } },
        // KEDO input density — slightly roomier than Aura's 0.5rem y for the taller KEDO fields.
        formField: { paddingX: "0.875rem", paddingY: "0.625rem" },
        focusRing: { width: "2px", style: "solid", color: "var(--kedo-color-blue-3)", offset: "0", shadow: "none" },
        // Gap between a control and the panel it opens. Select and the other
        // library overlays read it from here; BaseDropdown's Popover has its own
        // `gutter` token set to the same value below, so the two stay equal by
        // construction rather than by two defaults that happened to agree.
        anchorGutter: "0.25rem",
        colorScheme: {
            light: {
                primary: { color: "var(--kedo-color-blue-1)", inverseColor: "#ffffff", hoverColor: "var(--kedo-color-accent-blue)", activeColor: "var(--kedo-color-dark-2)" },
                highlight: { background: "var(--kedo-color-blue-5)", focusBackground: "var(--kedo-color-blue-4)", color: "var(--kedo-color-blue-1)", focusColor: "var(--kedo-color-blue-1)" },
                formField: {
                    background: "var(--kedo-color-white)",
                    borderRadius: "var(--kedo-radius-md)",
                    borderColor: "var(--kedo-color-light-2)",
                    hoverBorderColor: "var(--kedo-color-light-1)",
                    focusBorderColor: "var(--kedo-color-accent-blue)",
                    invalidBorderColor: "var(--kedo-color-accent-red)",
                    color: "var(--kedo-color-dark-2)",
                    placeholderColor: "var(--kedo-color-dark-light)",
                    floatLabelColor: "var(--kedo-color-dark-light)",
                    floatLabelFocusColor: "var(--kedo-color-accent-blue)",
                    floatLabelActiveColor: "var(--kedo-color-dark-light)",
                    iconColor: "var(--kedo-color-dark-light)",
                    disabledBackground: "var(--kedo-color-light-3)",
                    disabledColor: "var(--kedo-color-dark-light)",
                },
                // One surface radius across the app: a panel is a continuation of
                // the control it opens from, so overlays match the input and modal
                // radius (--kedo-radius-md) rather than sitting a step tighter.
                // Rows INSIDE a panel stay small on purpose — concentric radii want
                // the inner one smaller (16px panel − 4px inset = 12px > the 6px rows).
                overlay: { select: { borderRadius: "var(--kedo-radius-md)", shadow: "var(--kedo-shadow-dropdown)" }, popover: { borderRadius: "var(--kedo-radius-md)", shadow: "var(--kedo-shadow-dropdown)" }, modal: { borderRadius: "var(--kedo-radius-md)", shadow: "var(--kedo-shadow-dropdown)" } },
            },
        },
    },
    components: {
        // Per-component token overrides for the KEDO tab strip. Aura's defaults
        // diverge from the old KEDO strip on BOTH color and spacing:
        //   - color: active {primary.color}=blue-1 / inactive {text.muted.color}=gray
        //     → KEDO uses accent-blue active, dark-2 inactive.
        //   - spacing: Aura tab padding is `1rem 1.125rem` (16px TOP+bottom, 18px
        //     sides) → tall, spread tabs. The old KEDO strip used `pb-5` (10px bottom,
        //     no top) + `gap-10` (20px between tabs) on a 2px `light-2` bottom rail with
        //     a 2px accent-blue active underline. We restore that tighter KEDO rhythm:
        //     bottom-only padding + ~10px side padding (≈20px between tabs), the light-2
        //     rail, and a 2px accent-blue active underline overlapping it.
        //
        // The active underline is the active tab's own CSS bottom border
        // (`activeBorderColor`), NOT PrimeVue's moving `activeBar` element. The
        // activeBar is positioned by a JS width-measurement of the active tab, which
        // measures 0 when the strip mounts inside a hidden/animating modal — so the
        // underline was missing on first open until a tab was clicked. The CSS border
        // is measurement-free and always renders. The activeBar is disabled (height 0).
        tabs: {
            tablist: {
                borderColor: "var(--kedo-color-light-2)",
                borderWidth: "0 0 2px 0",
            },
            tab: {
                color: "var(--kedo-color-dark-2)",
                activeColor: "var(--kedo-color-accent-blue)",
                hoverColor: "var(--kedo-color-accent-blue)",
                fontWeight: "600",
                padding: "0.25rem 0.625rem 0.625rem 0.625rem",
                margin: "0 0 -2px 0",
                borderWidth: "0 0 2px 0",
                borderColor: "transparent",
                hoverBorderColor: "transparent",
                activeBorderColor: "var(--kedo-color-accent-blue)",
            },
            activeBar: {
                background: "transparent",
                height: "0",
            },
        },
        // KEDO accordions (BaseAccordion + the SidebarStage preset) are chrome-less
        // disclosures living inside surfaces that own their own background and
        // spacing — filter sidebars, cards. Aura's defaults assume a bordered,
        // white, self-contained panel, so four things are neutralised:
        //   - panel/header/content borders (Aura draws 1px dividers between panels;
        //     every KEDO accordion is a single panel, so the divider is pure noise —
        //     it is what the 4 raw `primevue/accordion` pages hack away with a local
        //     `:deep(.p-accordionpanel){border:none}`).
        //   - backgrounds → transparent, so a header does not paint a white box over
        //     a coloured sidebar; wrappers add their own hover (e.g. blue-5).
        //   - header/toggle-icon colour → dark-2. Aura uses {text.muted.color} (gray)
        //     for the collapsed state; KEDO section headers are always full-contrast.
        //   - padding → 10px, the KEDO `p-5` rhythm, instead of Aura's 18px.
        // Padding must come from the token, not a Tailwind utility on the header:
        // `.p-accordionheader` is `all: unset` + `padding: dt(...)`, and the primevue
        // CSS layer sits above tailwind's, so a utility class would lose.
        accordion: {
            panel: { borderWidth: "0" },
            header: {
                color: "var(--kedo-color-dark-2)",
                hoverColor: "var(--kedo-color-dark-2)",
                activeColor: "var(--kedo-color-dark-2)",
                activeHoverColor: "var(--kedo-color-dark-2)",
                background: "transparent",
                hoverBackground: "transparent",
                activeBackground: "transparent",
                activeHoverBackground: "transparent",
                borderWidth: "0",
                padding: "0.625rem",
                toggleIcon: {
                    color: "var(--kedo-color-dark-2)",
                    hoverColor: "var(--kedo-color-dark-2)",
                    activeColor: "var(--kedo-color-dark-2)",
                    activeHoverColor: "var(--kedo-color-dark-2)",
                },
            },
            content: {
                background: "transparent",
                color: "var(--kedo-color-dark-2)",
                borderWidth: "0",
                padding: "0 0.625rem 0.625rem 0.625rem",
            },
            // PrimeVue's own chevron is an SVG with hard-coded width/height="14"
            // and there is NO size design-token for it — `accordion.header.toggleIcon`
            // exposes colour only. 14px is oversized next to a dense `text-xs` row
            // (ChooseRoles / RolePermissionsContent), and the tighter header padding
            // above makes it read larger still. `1em` makes it scale with its row the
            // way BaseIcon already does inside BaseAccordion, so both icon systems
            // look the same wherever they meet. Aura ships no `css` for accordion,
            // so this is additive — nothing is overwritten.
            css: `
                .p-accordionheader-toggle-icon {
                    width: 1em;
                    height: 1em;
                }
            `,
        },
        // #17: a BaseDropdown panel and a Select panel must read as one system.
        // Rather than hand-matching values that then drift, the popover points at
        // the SAME semantic tokens Select's overlay uses — they are identical by
        // construction, and enriching the palette moves both at once. Panel
        // chrome therefore lives here, not in BaseDropdown's `contentClass`,
        // which is now layout-only.
        //
        // The arrow stays off: KEDO dropdowns never had one, and Select's panel
        // has none either. `gutter` is Aura's 10px, matching the old desktop gap.
        popover: {
            root: {
                // Only the gutter needs stating: the semantic block above already
                // gives overlay.select and overlay.popover the same radius and
                // shadow, so background/border/radius need no override here.
                // Aura leaves popover a 10px gap while every other overlay uses the
                // anchor gutter — that gap is what made a menu read as a detached,
                // rounder card next to a Select panel, since all four of its
                // corners were visible instead of two. Kept in step with
                // `semantic.anchorGutter`; Popover cannot read that token itself
                // (it calls absolutePosition with the gutter flag off and applies
                // its own margin instead), so the value is mirrored here.
                gutter: "0.25rem",
            },
            // {list.padding} is what Select insets its options by (4px, against the
            // old hand-rolled 10px), so option rows run nearly edge to edge and
            // their hover reads as a row highlight rather than a floating pill.
            content: { padding: "{list.padding}" },
            css: `
                .p-popover:before,
                .p-popover:after {
                    content: none;
                }
            `,
        },
        // The gap between a Select and its panel cannot be set through
        // `semantic.anchorGutter` in practice: PrimeVue resolves that value by
        // scanning stylesheets for a variable whose name ends in `-anchor-gutter`,
        // and when the lookup misses it assigns an EMPTY margin — i.e. flush —
        // rather than falling back to the 2px default (that fallback only exists
        // on the flipped branch). Setting the margin directly is not subject to
        // that lookup. Kept equal to `popover.gutter` so a Select panel and a
        // BaseDropdown menu sit the same distance from their trigger.
        select: {
            css: `
                .p-select-overlay {
                    margin-block-start: 0.25rem;
                }
            `,
        },
        datatable: {
            colorScheme: {
                light: {
                    headerCell: {
                        background: "var(--kedo-color-light-3)",
                        // dark-2 (#1D1929) gives ~14:1 contrast on light-3 (#F8F8F8),
                        // meeting WCAG AA. dark-light (#77757D) was 4.27:1 (below 4.5:1
                        // for 14px normal text).
                        color: "var(--kedo-color-dark-2)",
                        borderColor: "transparent",
                    },
                    bodyCell: {
                        // transparent removes the per-cell bottom border that
                        // fights the border-separate floating-row look in BaseDataTable.
                        borderColor: "transparent",
                    },
                    row: {
                        background: "var(--kedo-color-white)",
                        hoverBackground: "var(--kedo-color-blue-5)",
                        color: "var(--kedo-color-dark-2)",
                    },
                },
            },
        },
    },
});

// Russian locale for PrimeVue components (the app is single-locale `ru`). Covers
// the DatePicker calendar labels and the shared Today/Clear button bar.
const ruLocale = {
    firstDayOfWeek: 1,
    dayNames: ["Воскресенье", "Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота"],
    dayNamesShort: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
    dayNamesMin: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
    monthNames: [
        "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
        "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
    ],
    monthNamesShort: ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"],
    today: "Сегодня",
    clear: "Очистить",
    dateFormat: "dd.mm.yy",
    weekHeader: "Нед",
    // Empty-state strings shared by Tree/Select/etc; unset keys fall back to PrimeVue's English defaults.
    emptyMessage: "Нет доступных вариантов",
    emptyFilterMessage: "Ничего не найдено",
    emptySearchMessage: "Ничего не найдено",
    emptySelectionMessage: "Ничего не выбрано",
};

export const primeVueConfig = {
    theme: {
        preset: BrandPreset,
        options: {
            darkModeSelector: ".dark-mode-not-supported",
            cssLayer: {
                name: "primevue",
                order: "tailwind-base, primevue, tailwind-utilities",
            },
        },
    },
    locale: ruLocale,
};
